import { createAppRequest } from '@/services/request';
import type {
  MethodologyAiDiscoverRequest,
  MethodologyAiDiscoverResult,
  AiTagSuggestion,
  ArticleImportRequest,
  LibraryTaxonomyTagGroup,
  LibraryTaxonomyTagItem,
  MethodologyLibraryDetail,
  MethodologyLibraryItem,
  MethodologyLibraryListResponse,
  MethodologyLibrarySegment,
  MethodologyRecommendationResult,
  OutcomeReview,
} from './types/methodology-center';
import type { ZyCoTagGroupWithTags } from '@/modules/zy-consultant-operations/types';

const request = createAppRequest();
const BASE_PATH = '/bff/methodology-center/v2';

export const listLibraryItems = (
  library: MethodologyLibrarySegment,
  params?: Record<string, unknown>,
) =>
  request<MethodologyLibraryListResponse>(`${BASE_PATH}/${library}`, {
    method: 'GET',
    params,
  });

export const getLibraryItemDetail = (
  library: MethodologyLibrarySegment,
  uuid: string,
) =>
  request<MethodologyLibraryDetail>(`${BASE_PATH}/${library}/${uuid}`, {
    method: 'GET',
  });

export const createLibraryItem = (
  library: MethodologyLibrarySegment,
  data: Partial<MethodologyLibraryItem>,
) =>
  request<MethodologyLibraryItem>(`${BASE_PATH}/${library}`, {
    method: 'POST',
    data,
  });

export const aiDiscoverLibraryItems = (
  library: MethodologyLibrarySegment,
  data: MethodologyAiDiscoverRequest,
) =>
  request<MethodologyAiDiscoverResult>(`${BASE_PATH}/${library}/ai-discover`, {
    method: 'POST',
    data,
  });

export const updateLibraryItem = (
  library: MethodologyLibrarySegment,
  uuid: string,
  data: Partial<MethodologyLibraryItem>,
) =>
  request<MethodologyLibraryItem>(`${BASE_PATH}/${library}/${uuid}`, {
    method: 'PUT',
    data,
  });

export const removeLibraryItem = (library: MethodologyLibrarySegment, uuid: string) =>
  request<{ success: boolean }>(`${BASE_PATH}/${library}/${uuid}`, {
    method: 'DELETE',
  });

export const importArticleToLibrary = (
  library: MethodologyLibrarySegment,
  data: ArticleImportRequest,
) =>
  request<MethodologyLibraryItem>(`${BASE_PATH}/${library}/import-article`, {
    method: 'POST',
    data,
  });

export const publishLibraryItem = (library: MethodologyLibrarySegment, uuid: string) =>
  request<MethodologyLibraryItem>(`${BASE_PATH}/${library}/${uuid}/status`, {
    method: 'PATCH',
    data: { lifecycleStatus: 'published' },
  });

export const recommendSimilarCases = (data: {
  customerUuid: string;
  opportunityUuid?: string;
  industry?: string;
  customerType?: string;
  customerStage?: string;
  keyTags?: string[];
}) =>
  request<MethodologyRecommendationResult>(`${BASE_PATH}/recommend/similar-cases`, {
    method: 'POST',
    data,
  });

export const recommendBestPlaybook = (data: {
  customerUuid: string;
  opportunityUuid?: string;
  industry?: string;
  customerType?: string;
  customerStage?: string;
  keyTags?: string[];
}) =>
  request<MethodologyRecommendationResult>(`${BASE_PATH}/recommend/best-playbook`, {
    method: 'POST',
    data,
  });

export const recommendNextAction = (data: {
  customerUuid: string;
  opportunityUuid?: string;
  industry?: string;
  customerType?: string;
  customerStage?: string;
  keyTags?: string[];
}) =>
  request<MethodologyRecommendationResult>(`${BASE_PATH}/recommend/next-action`, {
    method: 'POST',
    data,
  });

export const submitRecommendationFeedback = (
  logUuid: string,
  data: {
    adopted?: boolean;
    adoptedSourceLibrary?: string;
    adoptedSourceUuid?: string;
    feedbackReason?: string;
  },
) =>
  request(`${BASE_PATH}/recommend/logs/${logUuid}/feedback`, {
    method: 'POST',
    data,
  });

export const generateOutcomeReview = (data: {
  customerUuid: string;
  opportunityUuid?: string;
}) =>
  request<OutcomeReview>(`${BASE_PATH}/outcome-review/generate`, {
    method: 'POST',
    data,
  });

export const confirmOutcomeReview = (
  reviewUuid: string,
  data: {
    outcomeType?: 'win' | 'loss' | 'ongoing';
    confirmedSummary?: string;
    structuredReasons?: Array<{ reason: string; category?: string; weight?: number }>;
  },
) =>
  request<OutcomeReview>(`${BASE_PATH}/outcome-review/${reviewUuid}/confirm`, {
    method: 'POST',
    data,
  });

export const aiMineLibraryItemTags = (library: MethodologyLibrarySegment, uuid: string) =>
  request<{ suggestedNames: string[]; model?: string | null }>(
    `${BASE_PATH}/${library}/${uuid}/mine-tags`,
    { method: 'POST' },
  );

export const aiMatchLibraryItemTags = (library: MethodologyLibrarySegment, uuid: string) =>
  request<{ matchedTagUuids: string[]; model?: string | null }>(
    `${BASE_PATH}/${library}/${uuid}/match-tags`,
    { method: 'POST' },
  );

export const getLibraryItemUnifiedTags = (library: MethodologyLibrarySegment, uuid: string) =>
  request<ZyCoTagGroupWithTags[]>(
    `${BASE_PATH}/${library}/${uuid}/unified-tags`,
    { method: 'GET' },
  );

export const saveLibraryItemUnifiedTags = (
  library: MethodologyLibrarySegment,
  uuid: string,
  tagUuids: string[],
) =>
  request<ZyCoTagGroupWithTags[]>(
    `${BASE_PATH}/${library}/${uuid}/unified-tags`,
    { method: 'PUT', data: { tagUuids } },
  );
